import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, Lock, Eye, FileWarning, Loader2, RefreshCw, Scale, Clock, Ban, LogOut } from 'lucide-react';
import useCameraViewport from '../utils/useCameraViewport';

// Iter. 406.fr.55 — fully i18n-driven (fr/en/es).
const ConsentScreen = ({ onAccepted, isReconsent = false }) => {
  const { t } = useTranslation();
  const { user, updateUser, logout } = useAuth();
  const [checkedNDA, setCheckedNDA] = useState(false);
  const [checkedRGPD, setCheckedRGPD] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Iter. 406.g — iOS-safe fullscreen shell with internal scroll
  const { containerStyle } = useCameraViewport({ open: true, allowInternalScroll: true });

  const allChecked = checkedNDA && checkedRGPD;

  const handleAccept = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post('/consent/accept');
      if (user) {
        const updatedUser = {
          ...user,
          consent_accepted: true,
          consent_version: response.data?.version || 'beta_v1',
        };
        updateUser(updatedUser);
        try { localStorage.setItem('user', JSON.stringify(updatedUser)); } catch { /* */ }
      }
      setTimeout(() => onAccepted(), 0);
    } catch (err) {
      console.error('Consent error:', err);
      const status = err?.response?.status;
      if (status === 401) {
        setError(t('consent.error_session_expired'));
      } else {
        setError(t('consent.error_generic'));
      }
      setSubmitting(false);
    }
  };

  const handleForceLogout = () => {
    localStorage.removeItem('session_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    if (logout) logout();
    window.location.href = '/';
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-background"
      style={containerStyle}
      data-testid="consent-screen"
    >
      <div className="max-w-lg w-full mx-auto px-4 py-6 min-h-full flex flex-col justify-center">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-xl mb-4 overflow-hidden">
            <img src="/icons/icon-192x192.png" alt="YUMIX" className="w-20 h-20 object-contain" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-foreground">
            YUMIX
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t('consent.brand_tagline')}
          </p>
        </div>

        {/* Reconsent Banner */}
        {isReconsent && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
            <p className="text-sm text-amber-200 text-center">
              <RefreshCw className="w-4 h-4 inline mr-2" />
              {t('consent.reconsent_banner')}
            </p>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 mb-4" data-testid="consent-error">
            <p className="text-sm text-destructive text-center mb-3">{error}</p>
            <Button
              onClick={handleForceLogout}
              variant="outline"
              size="sm"
              className="w-full border-destructive/30 text-destructive hover:bg-destructive/10"
              data-testid="consent-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('consent.btn_reconnect')}
            </Button>
          </div>
        )}

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="bg-destructive/10 border-b border-destructive/30 px-6 py-4">
            <h2 className="font-heading text-base sm:text-lg uppercase tracking-tight text-destructive flex items-center gap-2">
              <Scale className="w-5 h-5" />
              {t('consent.card_title')}
            </h2>
          </div>

          <div className="px-6 py-5 space-y-4 max-h-[40vh] sm:max-h-[50vh] overflow-y-auto">
            {/* RGPD / Law 25 Section */}
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-2">
              <h3 className="font-heading text-sm uppercase tracking-tight text-emerald-400 flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" />
                {t('consent.rgpd_title')}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t('consent.rgpd_body')}
              </p>
            </div>

            <p className="text-sm text-foreground leading-relaxed">
              {t('consent.intro_before_brand')}
              <span className="font-bold text-primary">YUMIX</span>
              {t('consent.intro_after_brand')}
              <span className="font-bold">{t('consent.intro_owner')}</span>
              {t('consent.intro_end')}
            </p>

            <div className="space-y-3">
              <div className="flex gap-3 p-3 bg-secondary/50 rounded-lg border-l-4 border-destructive">
                <Eye className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('consent.s1_title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('consent.s1_body')}</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-secondary/50 rounded-lg border-l-4 border-primary">
                <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('consent.s2_title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('consent.s2_body')}</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-secondary/50 rounded-lg border-l-4 border-amber-500">
                <Ban className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('consent.s3_title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('consent.s3_body')}</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-secondary/50 rounded-lg border-l-4 border-green-500">
                <Clock className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('consent.s4_title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('consent.s4_body_before_years')}
                    <span className="font-bold text-foreground">{t('consent.s4_body_years')}</span>
                    {t('consent.s4_body_after_years')}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-destructive/10 rounded-lg border border-destructive/30">
                <FileWarning className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-destructive">{t('consent.s5_title')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('consent.s5_body')}</p>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 p-3 rounded-lg mt-4">
              <p className="text-xs text-muted-foreground">
                <Lock className="w-3 h-3 inline mr-1" />
                {t('consent.signature_note')}
              </p>
            </div>
          </div>

          {/* Checkbox + Button */}
          <div className="px-6 py-5 border-t border-border space-y-4">
            {/* NDA Checkbox */}
            <label
              className="flex items-start gap-3 cursor-pointer select-none"
              data-testid="consent-checkbox-nda-label"
            >
              <Checkbox
                checked={checkedNDA}
                onCheckedChange={setCheckedNDA}
                className="mt-0.5 border-primary data-[state=checked]:bg-primary"
                data-testid="consent-checkbox-nda"
              />
              <span className="text-sm text-foreground leading-snug">
                <span className="font-bold">{t('consent.nda_checkbox_bold')}</span>
                {t('consent.nda_checkbox_rest')}
              </span>
            </label>

            {/* RGPD / Law 25 Checkbox */}
            <label
              className="flex items-start gap-3 cursor-pointer select-none"
              data-testid="consent-checkbox-rgpd-label"
            >
              <Checkbox
                checked={checkedRGPD}
                onCheckedChange={setCheckedRGPD}
                className="mt-0.5 border-emerald-500 data-[state=checked]:bg-emerald-500"
                data-testid="consent-checkbox-rgpd"
              />
              <span className="text-sm text-foreground leading-snug">
                <span className="font-bold">{t('consent.rgpd_checkbox_bold')}</span>
                {t('consent.rgpd_checkbox_rest_1')}
                <span className="text-primary underline">{t('consent.rgpd_checkbox_cgu')}</span>
                {t('consent.rgpd_checkbox_and')}
                <span className="text-primary underline">{t('consent.rgpd_checkbox_policy')}</span>
                {t('consent.rgpd_checkbox_rest_2')}
                <span className="font-bold text-emerald-400">{t('consent.rgpd_checkbox_data_bold')}</span>
                {t('consent.rgpd_checkbox_rest_3')}
              </span>
            </label>

            <Button
              onClick={handleAccept}
              disabled={!allChecked || submitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-heading uppercase tracking-wider text-base py-6 disabled:opacity-40"
              data-testid="consent-accept-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Scale className="w-5 h-5 mr-2" />
              )}
              {isReconsent ? t('consent.btn_accept_new') : t('consent.btn_sign')}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              {t('consent.footnote')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentScreen;
